import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Shield,
  Eye,
  TrendingUp,
  DollarSign,
  Lightbulb,
  Clock,
} from "lucide-react";
import { Button } from "components/ui/button";

const benefits = [
  {
    icon: Shield,
    title: "Protect your brand reputation",
    description: "Monitor and moderate harmful content around the clock",
  },
  {
    icon: Eye,
    title: "Discover audience insights",
    description: "Learn what resonates with your community in real-time",
  },
  {
    icon: TrendingUp,
    title: "Boost engagement metrics",
    description: "Enhance your social media ROI with smarter interactions",
  },
  {
    icon: DollarSign,
    title: "Identify revenue opportunities",
    description: "Uncover insights that can directly impact your bottom line",
  },
  {
    icon: Lightbulb,
    title: "Make data-driven decisions",
    description: "Turn community feedback into actionable business strategies",
  },
  {
    icon: Clock,
    title: "Reclaim valuable hours",
    description: "Focus on strategy while automation handles the details",
  },
];

const Benefits = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            How Moujib transforms your{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              social media workflow
            </span>
          </h2>
        </motion.div>

        <div
          ref={ref}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 max-w-6xl mx-auto"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-center"
        >
          <Button
            variant="outline"
            size="lg"
            className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all"
          >
            Learn More
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;
